import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../../shared/api.js';
import { ME_QUERY_KEY } from '../../../shared/auth.js';
import { Button } from '../../../ui/Button.js';
import { cx } from '../../../ui/cx.js';
import { ChevronDownIcon } from '../../../ui/icons.js';
import { clearLastAccess } from '../../../shared/last-access.js';
import { errorMessage } from '../../lib/errors.js';
import { useActiveMe } from '../../lib/me.js';
import { useEnterSession, useLeaveSupport } from '../../lib/session.js';
import { accessSummary, platformLabel, untilLabel } from './support-format.js';
import { ofName } from '../../lib/format.js';
import { useProductName } from '../../../public/platform.js';

const BAR = 'border-b border-author-support/30 bg-author-support-bg text-author-support-fg';

/**
 * Bandeau permanent de l'administrateur plateforme entré dans un garage (session support). Au
 * téléphone, **une seule ligne** (« Mode support · garage », « Détails », « Quitter ») ; le détail
 * (portée, échéance, « Rendre l'accès ») se déplie. Sur ordinateur, tout tient sur une ligne.
 * « Quitter » sort du mode support sans rendre l'accès (`platform.support.leave`) ; « Rendre l'accès »
 * (`platform.support.end`) demande une confirmation. En lecture seule, les écritures sont masquées par
 * les droits ; le serveur reste la référence (403 `support_read_only`).
 */
export function SupportModeBanner() {
  const me = useActiveMe();
  const s = me.supportSession;
  const enter = useEnterSession();
  const leave = useLeaveSupport();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const end = useMutation({
    mutationFn: () => api('platform.support.end', { params: { id: s?.accessId ?? '' }, body: {} }),
    onSuccess: (next) => {
      clearLastAccess();
      return enter(next, { to: '/admin' });
    },
  });
  if (!s) return null;
  const error = end.error ?? leave.error;
  const detail = (
    <>
      {accessSummary(s.scope, s.amountsVisible)} · {untilLabel(s.grantedUntil)}
    </>
  );
  const endControls = confirming ? (
    <span className="flex flex-wrap items-center gap-2" role="group" aria-label="Rendre l’accès">
      <span className="text-sm font-medium">L’accès prendra fin pour de bon.</span>
      <Button size="sm" variant="secondary" disabled={end.isPending} onClick={() => setConfirming(false)}>
        Annuler
      </Button>
      <Button size="sm" variant="danger" loading={end.isPending} onClick={() => end.mutate()}>
        Rendre maintenant
      </Button>
    </span>
  ) : (
    <Button size="sm" variant="quiet" onClick={() => setConfirming(true)}>
      Rendre l’accès
    </Button>
  );
  return (
    <div role="region" aria-label="Mode support" className={cx(BAR, 'px-[var(--page-x)] py-1 lg:py-2')}>
      <div className="mx-auto flex max-w-[1120px] items-center gap-2 lg:gap-4">
        <p className="min-w-0 flex-1 truncate text-sm">
          <span className="font-medium">Mode support</span> · {s.garageName}
          <span className="hidden lg:inline"> · {detail}</span>
        </p>
        <button
          type="button"
          aria-expanded={open}
          aria-controls="support-banner-detail"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex min-h-(--ctl-sm) shrink-0 items-center gap-1 rounded-lg px-2 text-sm font-medium underline-offset-4 hover:underline lg:hidden"
        >
          Détails
          <ChevronDownIcon size={16} className={cx('transition-transform', open && 'rotate-180')} />
        </button>
        <span className="hidden lg:flex">{endControls}</span>
        <Button size="sm" variant="secondary" loading={leave.isPending} onClick={() => leave.mutate()}>
          Quitter
        </Button>
      </div>
      {open ? (
        <div id="support-banner-detail" className="mx-auto max-w-[1120px] space-y-1 pb-2 lg:hidden">
          <p className="text-sm">{detail}</p>
          {endControls}
        </div>
      ) : null}
      {error ? <p className="mx-auto max-w-[1120px] pb-2 text-sm font-medium">{errorMessage(error)}</p> : null}
    </div>
  );
}

/**
 * Bandeau visible de toute l'équipe pendant un accès support accordé (`me.active.supportAccess`).
 * Le propriétaire (`support.manage`) peut retirer l'accès immédiatement.
 */
export function TeamSupportBanner() {
  const me = useActiveMe();
  const qc = useQueryClient();
  const access = me.active.supportAccess;
  const productNamePro = useProductName('pro');
  const revoke = useMutation({
    mutationFn: () => api('support.revoke', { params: { id: access?.id ?? '' } }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
      await qc.invalidateQueries({ queryKey: ['support'] });
    },
  });
  if (!access || me.supportSession) return null;
  const canRevoke = me.active.permissions.includes('support.manage');
  return (
    <div role="region" aria-label="Accès support en cours" className={cx(BAR, 'px-[var(--page-x)] py-2.5')}>
      <div className="mx-auto flex max-w-[1120px] flex-wrap items-center gap-x-4 gap-y-2">
        <p className="min-w-0 flex-1 text-sm">
          Le support {ofName(platformLabel(productNamePro))} a accès à votre garage ({accessSummary(access.scope, access.amountsVisible)}) {untilLabel(access.grantedUntil, 'jusqu’au')}.
        </p>
        {canRevoke ? (
          <Button size="sm" variant="secondary" loading={revoke.isPending} onClick={() => revoke.mutate()}>
            Retirer l’accès
          </Button>
        ) : null}
        {revoke.error ? <p className="w-full text-sm font-medium">{errorMessage(revoke.error)}</p> : null}
      </div>
    </div>
  );
}
